import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, X } from 'lucide-react';
import {
  EQUIPEMENT_CRITICITES,
  EQUIPEMENT_STATUTS,
  EQUIPEMENT_TYPES,
  createEquipementSchema,
  type CreateEquipementInput,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal CRUD équipement (US-EQ-02).
 *
 *  - Identité (code, désignation, numéros, fabricant, modèle).
 *  - Type / statut / criticité.
 *  - Localisation cascadante : Bâtiment → Étage → Salle.
 *  - Rattachement à un service + responsable.
 *  - Champ `est_mobile`.
 *
 * Le workflow d'affectation utilisateur N'EST PAS ici (action dédiée depuis
 * le drawer 360°). Custom_fields restent gérés via le Form Builder.
 */

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}
interface BatimentOption {
  id: string;
  code: string;
  nom: string;
}
interface EtageOption {
  id: string;
  code: string;
  numero: string;
  nom: string | null;
}
interface SalleOption {
  id: string;
  code: string;
  nom: string;
}
interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export function EquipementFormModal({
  equipementId,
  onClose,
  onSaved,
}: {
  equipementId?: string;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = !!equipementId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [batiments, setBatiments] = useState<BatimentOption[]>([]);
  const [etages, setEtages] = useState<EtageOption[]>([]);
  const [salles, setSalles] = useState<SalleOption[]>([]);
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateEquipementInput>({
    resolver: zodResolver(createEquipementSchema) as unknown as Resolver<CreateEquipementInput>,
    defaultValues: {
      type: 'production',
      statut: 'en_service',
      criticite: null,
      est_mobile: false,
    },
  });

  const watchedBatimentId = watch('batiment_id');
  const watchedEtageId = watch('etage_id');

  // Chargement initial des options (services / bâtiments / personnels).
  useEffect(() => {
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));

    api
      .get<{ data: BatimentOption[] }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => setBatiments([]));

    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
  }, []);

  // Cascade étages quand un bâtiment est sélectionné.
  useEffect(() => {
    if (!watchedBatimentId) {
      setEtages([]);
      return;
    }
    api
      .get<{ data: EtageOption[] }>(`/etages?batiment_id=${watchedBatimentId}`)
      .then((res) => setEtages(res.data.data))
      .catch(() => setEtages([]));
  }, [watchedBatimentId]);

  // Cascade salles quand un étage est sélectionné.
  useEffect(() => {
    if (!watchedEtageId) {
      setSalles([]);
      return;
    }
    api
      .get<{ data: SalleOption[] }>(`/salles?etage_id=${watchedEtageId}&page_size=200`)
      .then((res) => setSalles(res.data.data))
      .catch(() => setSalles([]));
  }, [watchedEtageId]);

  // Pré-remplit le code via /numbering/suggest en création.
  useEffect(() => {
    if (equipementId) return;
    api
      .get<{ data: { code: string } }>('/numbering/suggest?kind=equipement')
      .then((res) =>
        reset((prev) => ({ ...prev, code_equipement: res.data.data.code }) as typeof prev),
      )
      .catch(() => {
        // Silencieux : le user saisira manuellement.
      });
  }, [equipementId, reset]);

  // Chargement de l'équipement en mode édition.
  useEffect(() => {
    if (!equipementId) return;
    (async () => {
      try {
        const res = await api.get<{ data: CreateEquipementInput }>(`/equipements/${equipementId}`);
        const data = res.data.data;
        reset({
          code_equipement: data.code_equipement,
          numero_inventaire: data.numero_inventaire ?? null,
          code_barres: data.code_barres ?? null,
          numero_serie: data.numero_serie ?? null,
          designation: data.designation,
          type: data.type,
          statut: data.statut,
          criticite: data.criticite ?? null,
          fabricant: data.fabricant ?? null,
          modele: data.modele ?? null,
          service_id: data.service_id ?? null,
          batiment_id: data.batiment_id ?? null,
          etage_id: data.etage_id ?? null,
          salle_id: data.salle_id ?? null,
          responsable_id: data.responsable_id ?? null,
          est_mobile: data.est_mobile,
        });
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [equipementId, reset]);

  async function onSubmit(values: CreateEquipementInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        numero_inventaire: emptyToNull(values.numero_inventaire),
        code_barres: emptyToNull(values.code_barres),
        numero_serie: emptyToNull(values.numero_serie),
        fabricant: emptyToNull(values.fabricant),
        modele: emptyToNull(values.modele),
        criticite: emptyToNull(values.criticite),
        service_id: emptyToNull(values.service_id),
        batiment_id: emptyToNull(values.batiment_id),
        etage_id: emptyToNull(values.etage_id),
        salle_id: emptyToNull(values.salle_id),
        responsable_id: emptyToNull(values.responsable_id),
      };
      if (isEdit) {
        await api.patch(`/equipements/${equipementId}`, payload);
      } else {
        await api.post('/equipements', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!equipementId) return;
    if (!confirm('Supprimer cet équipement ? (soft delete)')) return;
    setSaving(true);
    try {
      await api.delete(`/equipements/${equipementId}`);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const personnelOptions = useMemo(
    () =>
      personnels
        .slice()
        .sort((a, b) => a.last_name.localeCompare(b.last_name))
        .map((p) => ({
          id: p.id,
          label: `${p.last_name} ${p.first_name}`,
        })),
    [personnels],
  );

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "Modifier l'équipement" : 'Nouvel équipement'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="equipement-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <Section title="Identité">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Code équipement *" error={errors.code_equipement?.message}>
                      <input
                        {...register('code_equipement')}
                        placeholder="EQ-001"
                        className={inputClass + ' uppercase'}
                      />
                    </Field>
                    <Field label="Désignation *" error={errors.designation?.message}>
                      <input {...register('designation')} className={inputClass} />
                    </Field>
                    <Field label="Numéro d'inventaire" error={errors.numero_inventaire?.message}>
                      <input {...register('numero_inventaire')} className={inputClass} />
                    </Field>
                    <Field label="Code-barres" error={errors.code_barres?.message}>
                      <input {...register('code_barres')} className={inputClass} />
                    </Field>
                    <Field label="Numéro de série" error={errors.numero_serie?.message}>
                      <input {...register('numero_serie')} className={inputClass} />
                    </Field>
                    <Field label="Fabricant" error={errors.fabricant?.message}>
                      <input {...register('fabricant')} className={inputClass} />
                    </Field>
                    <Field label="Modèle" error={errors.modele?.message} className="md:col-span-2">
                      <input {...register('modele')} className={inputClass} />
                    </Field>
                  </div>
                </Section>

                <Section title="Classification">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Type *" error={errors.type?.message}>
                      <select {...register('type')} className={inputClass}>
                        {EQUIPEMENT_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Statut *" error={errors.statut?.message}>
                      <select {...register('statut')} className={inputClass}>
                        {EQUIPEMENT_STATUTS.map((s) => (
                          <option key={s} value={s}>
                            {s.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Criticité" error={errors.criticite?.message}>
                      <select
                        {...register('criticite', {
                          setValueAs: (v) => (v === '' ? null : v),
                        })}
                        className={inputClass}
                      >
                        <option value="">— Aucune —</option>
                        {EQUIPEMENT_CRITICITES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </Section>

                <Section title="Localisation & affectation">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field
                      label="Service"
                      error={errors.service_id?.message}
                      className="md:col-span-2"
                    >
                      <select
                        {...register('service_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                        })}
                        className={inputClass}
                      >
                        <option value="">— Aucun service —</option>
                        {services.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.code} — {s.nom_complet}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Bâtiment" error={errors.batiment_id?.message}>
                      <select
                        {...register('batiment_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                          onChange: () => {
                            setValue('etage_id', null);
                            setValue('salle_id', null);
                          },
                        })}
                        className={inputClass}
                      >
                        <option value="">— Aucun —</option>
                        {batiments.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.code} — {b.nom}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Étage" error={errors.etage_id?.message}>
                      <select
                        {...register('etage_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                          onChange: () => setValue('salle_id', null),
                        })}
                        className={inputClass}
                        disabled={!watchedBatimentId}
                      >
                        <option value="">— Aucun —</option>
                        {etages.map((e) => (
                          <option key={e.id} value={e.id}>
                            Étage {e.numero}
                            {e.nom ? ` — ${e.nom}` : ''}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Salle" error={errors.salle_id?.message} className="md:col-span-2">
                      <select
                        {...register('salle_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                        })}
                        className={inputClass}
                        disabled={!watchedEtageId}
                      >
                        <option value="">— Aucune —</option>
                        {salles.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.code} — {s.nom}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label="Responsable"
                      error={errors.responsable_id?.message}
                      className="md:col-span-2"
                    >
                      <select
                        {...register('responsable_id', {
                          setValueAs: (v) => (v === '' ? null : v),
                        })}
                        className={inputClass}
                      >
                        <option value="">— Aucun —</option>
                        {personnelOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <label className="flex items-center space-x-2 text-sm text-gray-700 md:col-span-2">
                      <input
                        type="checkbox"
                        {...register('est_mobile')}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Équipement mobile</span>
                    </label>
                  </div>
                </Section>

                <p className="text-xs text-gray-500 pt-2">
                  Les champs avancés (custom_fields) seront rendus dynamiquement via le Form Builder
                  une fois un schéma "equipement" configuré.
                </p>
              </form>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  disabled={saving}
                >
                  Supprimer
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="equipement-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 p-3">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function emptyToNull<T>(v: T | null | undefined): T | null {
  if (v === undefined || v === null || v === '') return null;
  return v;
}
